import React, { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@atlaskit/modal-dialog';

import Row from 'components/Common/Row';
import Loading from 'components/Loading';
import Button from 'kingpin/atoms/Button';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import Alert from 'kingpin/Alert';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import FormInput from 'kingpin/forms/FormInput';
import Dropdown from 'components/Inputs/Dropdown';
import MetricPicker from 'components/Inputs/MetricPicker';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import { buildReportShow } from 'navigation/appRoutes';

import reportGeneratorTypeCheckers from 'types/reportGeneratorTypeCheckers';

import useFormState from './useFormState';
import {
  Breakdown,
  GenerateReportResponse,
  RegenerateReportRequest,
} from './types';

const GADGET_TYPE_OPTIONS = (
  current: Breakdown['gadgetType'],
  onChange: (v: Breakdown['gadgetType']) => void,
): DropdownOption[] => [
  {
    key: 'bar',
    label: 'Bar',
    isSelected: current === 'bar',
    onSelected: () => onChange('bar'),
  },
  {
    key: 'timeSeries',
    label: 'Line',
    isSelected: current === 'timeSeries',
    onSelected: () => onChange('timeSeries'),
  },
  {
    key: 'pie',
    label: 'Pie',
    isSelected: current === 'pie',
    onSelected: () => onChange('pie'),
  },
  {
    key: 'horizontalBar',
    label: 'Horizontal Bar',
    isSelected: current === 'horizontalBar',
    onSelected: () => onChange('horizontalBar'),
  },
  {
    key: 'stackedBar',
    label: 'Stacked Bar',
    isSelected: current === 'stackedBar',
    onSelected: () => onChange('stackedBar'),
  },
  {
    key: 'stackedArea',
    label: 'Stacked Area',
    isSelected: current === 'stackedArea',
    onSelected: () => onChange('stackedArea'),
  },
  {
    key: 'combo',
    label: 'Combo',
    isSelected: current === 'combo',
    onSelected: () => onChange('combo'),
  },
  {
    key: 'matrix',
    label: 'Matrix',
    isSelected: current === 'matrix',
    onSelected: () => onChange('matrix'),
  },
];

const LIMIT_OPTIONS = (
  current: Breakdown['limit'] | undefined,
  onChange: (v: Breakdown['limit']) => void,
): DropdownOption[] =>
  (['top5', 'top10', 'bottom5', 'bottom10'] as const).map((l) => ({
    key: l,
    label: l,
    isSelected: current === l,
    onSelected: () => onChange(l),
  }));

const GENERATE_METRIC_REPORT_PATH = '/v1/content/generateMetricReport';
const REGENERATE_METRIC_REPORT_PATH = '/v1/content/regenerateMetricReport';

const GenerateMetricReportForm = ({
  close,
  existingReport,
}: {
  close: () => void;
  existingReport?: ReportType;
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);

  const [supportingPickerKey, setSupportingPickerKey] = useState(0);

  const existingGeneratorConfig = existingReport?.generatorConfig;
  const initialConfig = reportGeneratorTypeCheckers.isMetricReportConfig(
    existingGeneratorConfig,
  )
    ? existingGeneratorConfig
    : undefined;

  const isDrifted = !!initialConfig?.isManuallyEdited;

  const {
    name,
    setName,
    metricId,
    onMetricSelected,
    dimensionFieldOptions,
    isSuggesting,
    suggestions,
    suggestError,
    onSuggestClicked,
    canSuggest,
    breakdowns,
    onBreakdownGadgetTypeChanged,
    onBreakdownLimitChanged,
    supportingMetricIds,
    supportingMetrics,
    sameDatasetMetricIds,
    onSupportingMetricAdded,
    onSupportingMetricRemoved,
    detailFields,
    detailFieldOptions,
    detailSortOptions,
    detailSortDirectionOptions,
    canGenerate,
    buildGenerateRequest,
  } = useFormState({ initialConfig, initialName: existingReport?.name });

  const onGenerateClicked = useCallback(async () => {
    const req = buildGenerateRequest();
    if (!req || isGenerating) {
      return;
    }

    setIsGenerating(true);
    try {
      if (existingReport) {
        const regenerateReq: RegenerateReportRequest = {
          ...req,
          reportId: existingReport.id,
        };
        const result = await api.post<GenerateReportResponse>(
          REGENERATE_METRIC_REPORT_PATH,
          regenerateReq,
        );
        if (!result.ok || !result.data) {
          throw new Error(result.problem ?? 'Request failed');
        }
        showToast('Report re-generated');
        navigate(buildReportShow({ id: existingReport.id, portal: undefined }));
      } else {
        const result = await api.post<GenerateReportResponse>(
          GENERATE_METRIC_REPORT_PATH,
          req,
        );
        if (!result.ok || !result.data) {
          throw new Error(result.problem ?? 'Request failed');
        }
        showToast('Report generated');
        navigate(
          buildReportShow({ id: result.data.reportId, portal: undefined }),
        );
      }
      close();
    } catch (err) {
      console.error('generateReport failed:', err);
      showToast('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [
    api,
    buildGenerateRequest,
    close,
    existingReport,
    isGenerating,
    navigate,
    showToast,
  ]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader
          title={
            existingReport ? 'Edit Metric Report' : 'Generate Metric Report'
          }
          onClose={close}
        />
        <FormContent>
          {isDrifted && (
            <div style={{ marginBottom: 8 }}>
              <Alert
                type={'Warning'}
                body={
                  'This report has been manually modified. Re-generating will reset those changes.'
                }
              />
            </div>
          )}
          <FormInput label={'Report Name'}>
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={'Enter report name'}
            />
          </FormInput>

          <FormInput label={'Metric'}>
            <MetricPicker
              metricId={metricId}
              setMetricId={() => {}}
              onMetricSelected={onMetricSelected}
              noSpecials
            />
          </FormInput>

          {metricId && (
            <FormInput label={'Supporting Metrics'}>
              <MetricPicker
                key={supportingPickerKey}
                metricId={undefined}
                setMetricId={() => {}}
                onMetricSelected={(metric) => {
                  if (metric) {
                    onSupportingMetricAdded(metric.id);
                    setSupportingPickerKey((k) => k + 1);
                  }
                }}
                noSpecials
                metricIdsToConstrainBy={sameDatasetMetricIds}
                metricIdRedList={[metricId, ...supportingMetricIds]}
                placeholder={'Add a supporting metric'}
              />
              {supportingMetrics.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  {supportingMetrics.map((metric) => (
                    <Row key={metric.id} spaceBetween centerAlign>
                      <Typography.Body type={'Body 12'}>
                        {metric.name}
                      </Typography.Body>
                      <Button
                        size={'Small'}
                        type={'Secondary'}
                        label={'Remove'}
                        onClick={() => onSupportingMetricRemoved(metric.id)}
                      />
                    </Row>
                  ))}
                </div>
              )}
            </FormInput>
          )}

          <FormInput label={'Breakdown Dimensions'}>
            <Dropdown
              options={dimensionFieldOptions}
              isMulti
              isSearchEnabled
              isDisabled={!metricId}
              placeholder={
                metricId
                  ? 'Select dimensions to slice the metric by'
                  : 'Select a metric first'
              }
            />
          </FormInput>

          <Row>
            <Button
              size={'Small'}
              type={'Secondary'}
              label={'Suggest Chart Types'}
              onClick={onSuggestClicked}
              isDisabled={!canSuggest}
              isLoading={isSuggesting}
            />
          </Row>

          {suggestError && (
            <div style={{ marginTop: 8 }}>
              <Alert type={'Warning'} body={suggestError} />
            </div>
          )}

          {isSuggesting && (
            <Row centerAlign style={{ marginTop: 16 }}>
              <div style={{ width: 32, height: 32 }}>
                <Loading isSmall />
              </div>
              <div style={{ marginLeft: 8 }}>
                <Typography.Body type={'Body 12'}>
                  Analysing field cardinality...
                </Typography.Body>
              </div>
            </Row>
          )}

          {suggestions && suggestions.length > 0 && (
            <>
              <div style={{ marginTop: 16 }}>
                <Typography.Body type={'Label'}>
                  Chart Suggestions
                </Typography.Body>
              </div>
              {breakdowns.map((breakdown, i) => (
                <div
                  key={breakdown.dimension}
                  style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    border: '1px solid var(--color-grey-8)',
                    borderRadius: 4,
                  }}
                >
                  <Typography.Body type={'Body 12'}>
                    {breakdown.dimension}
                    {suggestions[i]?.cardinality !== undefined
                      ? ` (${suggestions[i].cardinality} values)`
                      : ''}
                  </Typography.Body>
                  <Row centerAlign style={{ marginTop: 4, gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Dropdown
                        options={GADGET_TYPE_OPTIONS(
                          breakdown.gadgetType,
                          (v) => onBreakdownGadgetTypeChanged(i, v),
                        )}
                      />
                    </div>
                    {breakdown.gadgetType !== 'pie' && (
                      <div style={{ flex: 1 }}>
                        <Dropdown
                          options={LIMIT_OPTIONS(breakdown.limit, (v) =>
                            onBreakdownLimitChanged(i, v),
                          )}
                          placeholder={'No limit'}
                        />
                      </div>
                    )}
                  </Row>
                </div>
              ))}

              <div style={{ marginTop: 16 }}>
                <Typography.Body type={'Label'}>
                  Detail Table (optional)
                </Typography.Body>
              </div>
              <FormInput label={'Fields'}>
                <Dropdown
                  options={detailFieldOptions}
                  isMulti
                  isSearchEnabled
                  placeholder={'Select fields to include'}
                />
              </FormInput>
              {detailFields.length > 0 && (
                <>
                  <FormInput label={'Sort By'}>
                    <Dropdown options={detailSortOptions} />
                  </FormInput>
                  <FormInput label={'Sort Direction'}>
                    <Dropdown options={detailSortDirectionOptions} />
                  </FormInput>
                </>
              )}
            </>
          )}
        </FormContent>

        <Row spaceBetween>
          <Button
            size={'Small'}
            type={'Secondary'}
            label={'Cancel'}
            onClick={close}
            isDisabled={isGenerating}
          />
          <Button
            size={'Small'}
            type={'Primary'}
            label={existingReport ? 'Re-generate' : 'Generate'}
            onClick={onGenerateClicked}
            isLoading={isGenerating}
            isDisabled={!canGenerate || isGenerating || !suggestions}
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default GenerateMetricReportForm;
